import { HDate, months, gematriya } from '@hebcal/core';

/**
 * Chabad custom (siddur table): three extra chapters of Tehillim each day
 * from the second day of Rosh Chodesh Elul (1 Elul) through 9 Tishrei.
 * On Yom Kippur, 36 chapters are said in four groups of nine.
 */

const LAST_ELUL_DAY = 29;
const YOM_KIPPUR = 10;

export const YOM_KIPPUR_GROUPS = [
  { label: 'קודם כל נדרי', start: 115, end: 123 },
  { label: 'קודם השינה', start: 124, end: 132 },
  { label: 'אחר מוסף', start: 133, end: 141 },
  { label: 'אחר נעילה', start: 142, end: 150 },
];

/**
 * Day number in the daily 3-kapitels cycle (1–39), or null outside the period.
 * Day 39 is Yom Kippur (the 36-chapter day).
 * @param {Date} date
 * @returns {number|null}
 */
export function getThreeKapitelsDay(date) {
  const hd = new HDate(date);
  const month = hd.getMonth();
  const day = hd.getDate();

  if (month === months.ELUL) {
    return day;
  }
  if (month === months.TISHREI && day <= YOM_KIPPUR) {
    return LAST_ELUL_DAY + day;
  }
  return null;
}

/**
 * @param {Date} date
 * @returns {boolean}
 */
export function isThreeKapitelsPeriod(date) {
  return getThreeKapitelsDay(date) !== null;
}

/**
 * @param {Date} date
 * @returns {boolean}
 */
export function isYomKippurKapitelsDay(date) {
  const hd = new HDate(date);
  return hd.getMonth() === months.TISHREI && hd.getDate() === YOM_KIPPUR;
}

function chapterRange(start, end) {
  const chapters = [];
  for (let n = start; n <= end; n += 1) {
    chapters.push(n);
  }
  return chapters;
}

/**
 * The Tehillim chapter numbers for the given date, or null.
 * Regular days: three consecutive chapters. Yom Kippur: 115–150.
 * @param {Date} date
 * @returns {number[]|null}
 */
export function getThreeKapitelsChapters(date) {
  const dayNum = getThreeKapitelsDay(date);
  if (!dayNum) {
    return null;
  }
  if (isYomKippurKapitelsDay(date)) {
    return chapterRange(115, 150);
  }
  const start = (dayNum - 1) * 3 + 1;
  return [start, start + 1, start + 2];
}

function formatHebrewRange(start, end) {
  return `${gematriya(start)}–${gematriya(end)}`;
}

/**
 * Display lines for the kiosk (Hebrew only).
 * Regular days: Hebrew chapter numerals.
 * Yom Kippur: the four labeled groups from the siddur.
 * After tzeis the next Hebrew day's chapters are shown.
 * @param {Date} date
 * @param {boolean} isAfterTzeis
 * @returns {string[]}
 */
export function getThreeKapitelsContent(date, isAfterTzeis = false) {
  let targetDate = new Date(date);
  if (isAfterTzeis) {
    targetDate.setDate(targetDate.getDate() + 1);
  }

  if (!isThreeKapitelsPeriod(targetDate)) {
    return [];
  }

  if (isYomKippurKapitelsDay(targetDate)) {
    return YOM_KIPPUR_GROUPS.map(
      (group) => `${group.label}: ${formatHebrewRange(group.start, group.end)}`
    );
  }

  const chapters = getThreeKapitelsChapters(targetDate);
  if (!chapters) {
    return [];
  }

  return [chapters.map((n) => gematriya(n)).join(' – ')];
}
